import {
    AddIcon,
    Button,
    ButtonIcon,
    ButtonText,
    FormGroup,
    useListingService
} from "@hosttools/frontend";
import type {Listing} from "@hosttools/frontend/models/listing";
import type React from "react";
import {memo, useCallback, useEffect, useMemo, useState} from "react";
import {useController, useFormContext, useWatch} from "react-hook-form";

import type {ListingAttributesWithQuantity} from "../type";
import useHandleArrayField from "../useHandleArrayField";

import Form from "./Form";
import Item from "./Item";

import type {OptionType} from "@/client/components/Select";
import {Table, TableHeader, TableRow} from "@/client/components/Table";

const AttributeWithQuantity: React.FC = () => {
    const {control, trigger} = useFormContext<Listing>();
    const {
        fields,
        obj,
        isOpen,
        index: indexEditting,
        onOpen,
        onEdit,
        onRemove,
        onSave,
        onCancel
    } = useHandleArrayField("attributesWithQuantity");
    const listingService = useListingService();

    const {
        formState: {errors}
    } = useController<Listing>({
        control,
        name: "attributesWithQuantity",
        rules: {
            validate: useCallback(() => {
                if (isOpen) {
                    return "Please save or close form to continue";
                }
                return true;
            }, [isOpen])
        }
    });
    const attributes = useWatch({
        control,
        name: "attributesWithQuantity"
    });

    const [attributeOptions, setAttributeOptions] = useState<
        OptionType<ListingAttributesWithQuantity["attributeId"]>[]
    >([]);

    const attributeOptionsSelect = useMemo(() => {
        const selectedAttributeIds = (attributes || []).map(item => item.attributeId);

        return attributeOptions.filter(item => !selectedAttributeIds.includes(item.value));
    }, [attributeOptions, attributes]);

    useEffect(() => {
        if (!isOpen) {
            trigger("attributesWithQuantity");
        }
    }, [trigger, isOpen]);

    useEffect(() => {
        async function fetchOptions() {
            const data = await listingService.getListingAttributeOptions();
            setAttributeOptions(data);
        }
        fetchOptions();
    }, [listingService]);

    return (
        <FormGroup
            isInsideTab
            error={errors.attributesWithQuantity?.root?.message}
            label="Attributes"
            name="attributesWithQuantity.root"
        >
            {!isOpen && (
                <Button className="self-end" variant="ghost" onPress={onOpen}>
                    <ButtonIcon as={AddIcon} />
                    <ButtonText>Attribute</ButtonText>
                </Button>
            )}
            {(fields.length > 0 || isOpen) && (
                <Table overflow="visible" size="sm">
                    <TableRow>
                        <TableHeader>Attribute</TableHeader>
                        <TableHeader>Quantity</TableHeader>
                        <TableHeader align="end">Action</TableHeader>
                    </TableRow>
                    {fields.map((field, index) =>
                        index === indexEditting ? (
                            <Form
                                key={field._id}
                                attributeOptions={attributeOptions}
                                item={field}
                                onCancel={onCancel}
                                onChange={onSave}
                            />
                        ) : (
                            <Item
                                key={field._id}
                                attributeOptions={attributeOptions}
                                index={index}
                                item={field}
                                onEdit={onEdit}
                                onRemove={onRemove}
                            />
                        )
                    )}
                    {isOpen && !obj && (
                        <Form
                            attributeOptions={attributeOptionsSelect}
                            onCancel={onCancel}
                            onChange={onSave}
                        />
                    )}
                </Table>
            )}
        </FormGroup>
    );
};

export default memo(AttributeWithQuantity);
